import { useLoginStore } from '@/stores/login';
import { UserType } from '@/types';
import * as AmazonCognitoIdentity from 'amazon-cognito-identity-js';

export const AuthState = {
  /** 未認証（初期状態） */
  Unauthorized: 'Unauthorized',
  /** パスワード変更要求（初回ログイン時） */
  NewPasswordRequired: 'NewPasswordRequired',
  /** シークレットコード発行（TOTP初回認証時） */
  AssociateSecretCode: 'AssociateSecretCode',
  /** ワンタイムパスワード要求 */
  TotpRequired: 'TotpRequired',
  /** パスワード忘れ(認証コード発行要求) */
  ForgotPassword: 'ForgotPassword',
  /** パスワード忘れ(パスワード入力) */
  ConfirmPassword: 'ConfirmPassword',
  /** 認証成功 */
  Success: 'Success',
  /** 認証失敗 */
  Failure: 'Failure'
};

export interface IAuthCallback {
  onSuccess(): void;
  onFailure(error: string): void;
  newPasswordRequired(): void;
  associateSecretCode(url: string): void;
  totpRequired(): void;
}

export interface IForgotPasswordCallback {
  onSuccess(data: any): void;
  onFailure(err: Error): void;
  inputVerificationCode(data: any): void;
}

export interface IConfirmPasswordCallback {
  onSuccess(success: string): void;
  onFailure(err: Error): void;
}

/**
 * Cognito認証クラス
 */
export class Authentication {
  cognitoUser: AmazonCognitoIdentity.CognitoUser = null;
  callbacks: AmazonCognitoIdentity.IAuthenticationCallback;

  poolData: AmazonCognitoIdentity.ICognitoUserPoolData = {
    UserPoolId: '',
    ClientId: ''
  };

  /**
   * コンストラクタ
   * @param isAdmin
   */
  constructor(isAdmin: boolean) {
    this.poolData = getCognitoUserPoolData(isAdmin);
  }

  /**
   * ユーザー認証
   * @param userName
   * @param password
   */
  authenticateUser(userName: string, password: string, callback: IAuthCallback) {
    const userPool = new AmazonCognitoIdentity.CognitoUserPool(this.poolData);

    const authenticationData = {
      Username: userName,
      Password: password
    };

    const authenticationDetails = new AmazonCognitoIdentity.AuthenticationDetails(
      authenticationData
    );

    const userData = {
      Username: userName,
      Pool: userPool
    };

    this.cognitoUser = new AmazonCognitoIdentity.CognitoUser(userData);

    const _cognitoUser = this.cognitoUser;

    const _callbacks = {
      onSuccess: function (result: any) {
        // 認証成功時
        console.debug('[cognito]onSuccess');

        // IDトークン保存
        useLoginStore().setToken(result.getIdToken().getJwtToken());

        callback.onSuccess();
      },
      newPasswordRequired: function (userAttributes: any, requiredAttributes: any) {
        // パスワード変更要求
        console.debug('[cognito]newPasswordRequired');

        callback.newPasswordRequired();
      },
      mfaSetup: function (challengeName: any, challengeParameters: any) {
        console.debug('[cognito]mfaSetup');
        _cognitoUser.associateSoftwareToken(this);
      },
      associateSecretCode: function (secretCode: string) {
        console.debug('[cognito]associateSecretCode');

        let url =
          'otpauth://totp/' + userName + '?secret=' + secretCode + '&issuer=Cognito-TOTP-MFA';

        callback.associateSecretCode(url);
      },
      totpRequired: function (secretCode: string) {
        console.debug('[cognito]totpRequired');

        callback.totpRequired();
      },
      onFailure: function (err: any) {
        console.debug('[cognito]onFailure');
        console.debug(`[cognito] ${err}`);

        callback.onFailure(err);
      }
    };

    this.callbacks = _callbacks;

    this.cognitoUser.authenticateUser(authenticationDetails, _callbacks);
  }

  /**
   * パスワードを変更する
   * @param password 新しいパスワード
   */
  changePassword(password: string) {
    this.cognitoUser?.completeNewPasswordChallenge(password, {}, this.callbacks);
  }

  /**
   * ワンタイムパスワード認証
   * @param totp ワンタイムパスワード
   * @param first 初回認証
   */
  authenticateTotp(totp: string, first?: boolean) {
    if (first) {
      this.cognitoUser?.verifySoftwareToken(totp, 'MON-COM TOTP device', this.callbacks);
    } else {
      this.cognitoUser?.sendMFACode(totp, this.callbacks, 'SOFTWARE_TOKEN_MFA');
    }
  }

  /**
   * パスワード忘れ・認証コード発行
   * @param email ユーザID=e-mail
   */
  forgotPassword(userName: string, callback: IForgotPasswordCallback) {
    const userPool = new AmazonCognitoIdentity.CognitoUserPool(this.poolData);

    const userData = {
      Username: userName,
      Pool: userPool
    };

    this.cognitoUser = new AmazonCognitoIdentity.CognitoUser(userData);
    this.cognitoUser.forgotPassword(callback);
  }

  /**
   * パスワード忘れ・新パスワード設定
   * @param userName ユーザID = e-mail
   * @param verificationCode 認証コード
   * @param newPassword 新パスワード
   * @param callback コールバック
   */
  confirmPassword(
    userName: string,
    verificationCode: string,
    newPassword: string,
    callback: IConfirmPasswordCallback
  ) {
    const userPool = new AmazonCognitoIdentity.CognitoUserPool(this.poolData);

    const userData = {
      Username: userName,
      Pool: userPool
    };

    this.cognitoUser = new AmazonCognitoIdentity.CognitoUser(userData);
    this.cognitoUser.confirmPassword(verificationCode, newPassword, callback);
  }
}

export async function updateToken(): Promise<void> {
  const poolData: AmazonCognitoIdentity.ICognitoUserPoolData = getCognitoUserPoolData(
    useLoginStore().isAdmin
  );

  const userPool = new AmazonCognitoIdentity.CognitoUserPool(poolData);
  let cognitoUser = userPool.getCurrentUser();

  return new Promise((resolve, reject) => {
    if (cognitoUser) {
      cognitoUser.getSession(function (
        err: Error | null,
        session: AmazonCognitoIdentity.CognitoUserSession
      ) {
        if (session?.isValid()) {
          console.debug('[cognito] セッション有効');
          const token = session.getIdToken().getJwtToken();
          useLoginStore().setToken(token);
          resolve();
        } else {
          console.debug('[cognito] セッション無効');
          reject();
        }
      });
    } else {
      console.debug('[cognito] cognitoUser=null');
      reject();
    }
  });
}

export async function signOut(): Promise<void> {
  const poolData: AmazonCognitoIdentity.ICognitoUserPoolData = getCognitoUserPoolData(
    useLoginStore().isAdmin
  );

  const userPool = new AmazonCognitoIdentity.CognitoUserPool(poolData);
  let cognitoUser = userPool.getCurrentUser();

  if (cognitoUser) {
    cognitoUser?.signOut();
    console.debug('[cognito] sign out');
  } else {
    console.debug('[cognito] no user sign in');
  }
}

const getCognitoUserPoolData = (isAdmin: boolean): AmazonCognitoIdentity.ICognitoUserPoolData => {
  return {
    UserPoolId: isAdmin
      ? process.env.VITE_COGNITO_USERPOOL_ID_ADMIN!
      : process.env.VITE_COGNITO_USERPOOL_ID!,
    ClientId: isAdmin
      ? process.env.VITE_COGNITO_CLIENT_ID_ADMIN!
      : process.env.VITE_COGNITO_CLIENT_ID!
  };
};
